import { Component, ViewChild, ChangeDetectorRef, ElementRef, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatRadioChange } from '@angular/material/radio';
import { SfzDialogComponent } from 'app/shared/sfz-dialog/sfz-dialog.component';
import { ExtratoProcessamentoEfdService } from './extrato-processamento-efd.service';
import { CustomMonthYearValidator } from 'app/shared/CustomMonthYearValidator';
import DateUtils from '../../shared/model/date-util';
import { definicaoColunas, tituloColuna } from './Tabela-definicoes';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  RelatorioEntregasArquivosEfdService,
  RelatorioEntregasArquivosEfdPayload
} from './relatorio-entregas-arquivos-efd.service';
import { IExtratoEfdView } from 'app/shared/model/extrato-efd.model';
import RelatorioUtils from 'app/shared/sefaz/relatorio-util';

interface Opcao {
  texto: string;
  valor: string;
}

interface Categoria {
  nome: string;
  valorSelecionado: Opcao | null;
}

@Component({
  selector: 'app-extrato-processamento-efd',
  templateUrl: './extrato-processamento-efd.component.html',
  styleUrls: ['./extrato-processamento-efd.component.scss']
})
export class ExtratoProcessamentoEfdComponent implements OnInit {

  tituloColuna = tituloColuna;
  definicaoColunas = definicaoColunas;

  dataSource = new MatTableDataSource<IExtratoEfdView>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('numeroDocumento') numeroDocumentoInput!: ElementRef<HTMLInputElement>;

  totalElementos = 0;
  pageSize = 10;
  pageIndex = 0;
  sortColumn = 'competencia';
  sortDirection: 'asc' | 'desc' = 'desc';

  opcoes: Opcao[] = [
    { texto: 'Ativo', valor: 'A' },
    { texto: 'Inativo', valor: 'I' },
    { texto: 'Todos', valor: '' }
  ];

  categorias: Categoria[] = [
    { nome: '', valorSelecionado: this.opcoes.find(opcao => opcao.texto === 'Ativo') ?? null }
  ];

  numeroDocumentoControl = new FormControl('', [
    Validators.required,
    Validators.minLength(8),
    Validators.maxLength(18)
  ]);
  periodoInicialControl = new FormControl('', [Validators.required, CustomMonthYearValidator.dateFormatValidator()]);
  periodoFinalControl = new FormControl('', [Validators.required, CustomMonthYearValidator.dateFormatValidator()]);
  razaoSocialControl = new FormControl('');

  consultarHabilitado = false;
  exibirResultado = false;
  indSituacaoSelecionado = 'A';
  numeroDocumentoAnterior: string | null = null;
  mensagemErroContribuinte = '';
  modalRef?: NgbModalRef;

  constructor(
    private spinner: NgxSpinnerService,
    private extratoService: ExtratoProcessamentoEfdService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private relatorioEntregasArquivosEfdService: RelatorioEntregasArquivosEfdService
  ) { }

  ngOnInit(): void {
    this.inicializarPeriodosPadrao();
    this.podeConsultar();
    this.executarConsulta(0, this.indSituacaoSelecionado);
  }

  podeConsultar(): void {
    this.consultarHabilitado = this.todosOsCamposEstaoValidos();
  }

  todosOsCamposEstaoValidos(): boolean {
    this.periodosSaoValidosEPreenchidos();

    if (this.todosOsCamposEstaoPreenchidos()) {
      return this.periodoInicialControl.valid && this.periodoFinalControl.valid;
    }
    return false;
  }

  todosOsCamposEstaoPreenchidos(): boolean {
    return !this.verificarVazio(this.periodoInicialControl.value)
        && !this.verificarVazio(this.periodoFinalControl.value);
  }

  consultar(): void {
    if (!this.consultarHabilitado) {
      return;
    }

    if (!this.validarPeriodos()) {
      return;
    }

    this.limparTabela();
    this.executarConsulta(0, this.indSituacaoSelecionado);
  }

  executarConsulta(pageIndex: number, indSituacao: string = this.indSituacaoSelecionado): void {
    const inicio = this.periodoInicialControl.value === null ? null : DateUtils.formatarDataString_MM_AAAA_Para_AAAAMM(this.periodoInicialControl.value);
    const final = this.periodoFinalControl.value === null ? null : DateUtils.formatarDataString_MM_AAAA_Para_AAAAMM(this.periodoFinalControl.value);

    const parametros = {
      competenciaInicial: inicio,
      competenciaFinal: final,
      indicadorSituacao: indSituacao,
      page: pageIndex,
      size: this.pageSize,
      sort: [`${this.sortColumn},${this.sortDirection}`]
    };

    this.spinner.show();

    this.extratoService.query(parametros)
      .pipe(
        finalize((): void => {
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (data) => {
          this.dataSource.data = data.content.map(item => ({
            sequencial: item.seqExtratoProcessamento,
            competencia: item.competencia,
            numeroCnpj: item.cnpj,
            numeroCaceal: item.caceal != null ? item.caceal.toString() : null,
            statusSolicitacao: item.situacaoSolicitacao,
            statusExtrato: item.situacaoProcExtrato,
            situacaoExtrato: item.situacaoExtrato,
            dataGeracao: DateUtils.formatarIsoParaDataHora(item.dataGeracao),
            txtObservacao: item.txtObservacao
          }));

          this.totalElementos = data.totalElements;
          this.pageIndex = pageIndex;
          this.exibirResultado = true;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.dataSource.data = [];
          this.totalElementos = 0;
          if (err.status === 403) {
            this.openDialog('Permissão negada', err.error?.titulo ?? 'Sem permissão', err.error?.detalhe ?? '');
          } else {
            this.openDialog('Erro', 'Erro ao consultar extrato de processamento', '');
          }
        }
      });
  }

  aoMudarPagina(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.executarConsulta(this.pageIndex, this.indSituacaoSelecionado);
  }

  aoOrdenar(event: Sort): void {
    this.sortColumn = event.active;
    this.sortDirection = event.direction === 'asc' ? 'asc' : 'desc';
    this.pageIndex = 0;
    this.paginator.pageIndex = 0;
    this.executarConsulta(this.pageIndex, this.indSituacaoSelecionado);
  }

  baixarRelatorioEntregasArquivosEfd(row: IExtratoEfdView): void {
    const dadosRelatorio = this.montarDadosRelatorio(row);
    if (!dadosRelatorio) {
      this.toastr.error('Dados insuficientes para gerar o relatório de entregas de arquivos EFD.', 'Erro');
      return;
    }

    this.spinner.show();

    this.relatorioEntregasArquivosEfdService.gerarRelatorio(dadosRelatorio.payload)
      .pipe(
        finalize((): void => {
          void this.spinner.hide();
        })
      )
      .subscribe({
        next: response => this.tratarSucessoRelatorio(response, dadosRelatorio.competenciaOriginal),
        error: err => this.tratarErroRelatorio(err)
      });
  }

  extrairNomeArquivo(contentDisposition: string): string | null {
    const match = contentDisposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]*)/i);
    return match ? match[2] : null;
  }

  limparTabela(): void {
    this.dataSource.data = [];
    this.totalElementos = 0;
    this.pageIndex = 0;
    this.cdr.detectChanges();
  }

  selecionarOpcao(categoria: Categoria, event: unknown): void {
    const opcao = (event as MatRadioChange).value as Opcao;
    categoria.valorSelecionado = opcao;
    this.pageIndex = 0;
    this.indSituacaoSelecionado = opcao.valor;
    this.paginator.pageIndex = 0;
    this.executarConsulta(this.pageIndex, opcao.valor);
  }

  validarPeriodos(): boolean {
    if (this.exibirErroDeValidacaoPeriodo(this.periodoInicialControl)) {
      return false;
    }

    if (this.exibirErroDeValidacaoPeriodo(this.periodoFinalControl)) {
      return false;
    }

    return this.validarOrdemPeriodos();
  }

  limpar(): void {
    this.periodoInicialControl.reset();
    this.periodoFinalControl.reset();
    this.razaoSocialControl.setValue('');
    this.consultarHabilitado = false;
    this.mensagemErroContribuinte = '';
    this.exibirResultado = false;
    this.limparTabela();
  }

  private periodosSaoValidosEPreenchidos(): boolean {
    return !this.verificarVazio(this.periodoInicialControl.value)
      && !this.verificarVazio(this.periodoFinalControl.value)
      && this.periodoInicialControl.valid
      && this.periodoFinalControl.valid;
  }

  private inicializarPeriodosPadrao(): void {
    const hoje = new Date();
    const periodoFinal = this.formatarMesAno(hoje);

    const periodoInicialDate = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    periodoInicialDate.setMonth(periodoInicialDate.getMonth() - 12);
    const periodoInicial = this.formatarMesAno(periodoInicialDate);

    this.periodoInicialControl.setValue(periodoInicial);
    this.periodoFinalControl.setValue(periodoFinal);
  }

  private formatarMesAno(data: Date): string {
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${mes}${ano}`;
  }

  private verificarVazio(valor: string | null | undefined): boolean {
    return !valor || valor.trim().length === 0;
  }

  private montarDadosRelatorio(row: IExtratoEfdView): { payload: RelatorioEntregasArquivosEfdPayload; competenciaOriginal: string } | null {
    const numeroDocumento = this.obterNumeroDocumentoRelatorio(row);
    const competenciaOriginal = row.competencia ?? '';
    const competencia = this.normalizarCompetenciaRelatorio(competenciaOriginal);
    const sequencial = row.sequencial;
    if (!numeroDocumento || !competencia) {
      return null;
    }

    return {
      payload: { numeroDocumento, competencia, sequencial },
      competenciaOriginal
    };
  }

  private obterNumeroDocumentoRelatorio(row: IExtratoEfdView): string {
    const numeroDocumento = row.numeroCnpj ?? this.numeroDocumentoControl.value?.toString() ?? '';
    return numeroDocumento ? this.normalizarNumeroDocumentoAlfanumerico(numeroDocumento) : '';

  }

  /**
   * Normaliza um número de documento preservando letras e dígitos.
   * Remove apenas caracteres de formatação (espaços, pontos, barras, traços, parênteses, etc.).
   */
  private normalizarNumeroDocumentoAlfanumerico(valor: string): string {
    return valor.replace(/[^A-Za-z0-9]/g, '');
  }

  private normalizarCompetenciaRelatorio(competenciaOriginal: string): string {
    return competenciaOriginal.replace(/\//g, '');
  }

  private tratarSucessoRelatorio(response: any, competenciaOriginal: string): void {
    const contentDisposition = response.headers.get('content-disposition') ?? '';
    const nomeArquivo = this.extrairNomeArquivo(contentDisposition) ?? `relatorio-entregas-arquivos-efd-${competenciaOriginal.replace(/\//g, '-')}.pdf`;
    RelatorioUtils.downloadPdf(response.body, nomeArquivo).subscribe();
  }

  private tratarErroRelatorio(err: any): void {
    if (err.status === 403) {
      this.openDialog('Permissão negada', err.error?.titulo ?? 'Sem permissão', err.error?.detalhe ?? '');
      return;
    }

    const erro = this.extrairErroResponse(err);
    const mensagem = erro?.description ?? erro?.message ?? 'Ocorreu um erro ao baixar o relatório de entregas de arquivos EFD.';
    this.toastr.error(mensagem, 'Erro');
  }

  private extrairErroResponse(err: any): any {
    if (err.error instanceof ArrayBuffer) {
      try {
        const texto = new TextDecoder().decode(err.error);
        return JSON.parse(texto);
      } catch {
        return null;
      }
    }
    return err.error;
  }

  private exibirErroDeValidacaoPeriodo(control: FormControl<string | null>): boolean {
    if (!control.invalid) {
      return false;
    }
    this.openDialog('Período inválido', control.errors?.invalidDate ?? 'Período inválido', '');
    return true;
  }

  private validarOrdemPeriodos(): boolean {
    const periodoInicial = this.periodoInicialControl.value;
    const periodoFinal = this.periodoFinalControl.value;

    if (!periodoInicial || !periodoFinal) {
      return this.periodosBaseSaoValidos();
    }

    if (this.periodoInicialMaiorQueFinal(periodoInicial, periodoFinal)) {
      this.openDialog('Período inválido', 'Período Inicial não pode ser maior que o Final', '');
      return false;
    }

    return this.periodosBaseSaoValidos();
  }

  private periodosBaseSaoValidos(): boolean {
    return this.periodoInicialControl.valid && this.periodoFinalControl.valid;
  }

  private periodoInicialMaiorQueFinal(periodoInicial: string, periodoFinal: string): boolean {
    const periodoInicioAnoMes = DateUtils.converterDeMMAAAAParaAAAAMM(periodoInicial);
    const periodoTerminoAnoMes = DateUtils.converterDeMMAAAAParaAAAAMM(periodoFinal);
    return periodoInicioAnoMes > periodoTerminoAnoMes;
  }

  private openDialog(titulo: string, mensagem: string, detalhe: string): void {
    this.dialog.open(SfzDialogComponent, {
      data: { titulo, mensagem, detalhe }
    });
  }
}
